import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, BadgeCheck, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function VerifyPhotoDialog({ open, onOpenChange, onVerified }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [pose, setPose] = useState("");
  const [shot, setShot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [camErr, setCamErr] = useState(false);

  const stop = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };

  const start = async () => {
    setShot(null); setResult(null); setCamErr(false);
    try {
      setPose((await api.post("/verify/challenge")).data.pose);
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = s;
      if (videoRef.current) videoRef.current.srcObject = s;
    } catch { setCamErr(true); }
  };

  useEffect(() => { if (open) start(); else stop(); return stop; }, [open]);

  const capture = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    const c = document.createElement("canvas");
    const scale = Math.min(1, 720 / v.videoWidth);
    c.width = v.videoWidth * scale; c.height = v.videoHeight * scale;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    setShot(c.toDataURL("image/jpeg", 0.85));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/verify/selfie", { image_base64: shot });
      setResult(data);
      if (data.verified) { stop(); toast.success("You're photo verified!"); onVerified?.(); }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="verify-dialog" className="glass border-sky-400/30 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><BadgeCheck className="h-5 w-5 text-sky-300" /> Verify your photo</DialogTitle>
          <DialogDescription className="text-slate-300">Take a quick live selfie doing the pose below. AI checks it matches your profile photo.</DialogDescription>
        </DialogHeader>
        {result?.verified ? (
          <div data-testid="verify-success" className="text-center py-6">
            <BadgeCheck className="h-14 w-14 text-sky-300 mx-auto" />
            <p className="font-display font-black text-2xl mt-3">You're verified</p>
            <p className="text-sm text-slate-300 mt-1">Your matches now see the blue badge on your profile.</p>
            <Button data-testid="verify-done" onClick={() => onOpenChange(false)} className="btn-gold rounded-full mt-5 px-8">Done</Button>
          </div>
        ) : (
          <>
            <div className="rounded-xl bg-sky-500/10 border border-sky-400/30 px-4 py-2.5 text-center">
              <p className="text-[10px] uppercase tracking-widest text-sky-300/80">Your pose</p>
              <p data-testid="verify-pose" className="font-display font-bold text-lg">{pose || "…"}</p>
            </div>
            <div className="relative rounded-2xl overflow-hidden bg-black aspect-[4/3]">
              {camErr ? (
                <div className="h-full flex items-center justify-center text-center text-sm text-pink-200 p-6">Camera blocked — allow camera access in your browser to verify.</div>
              ) : shot ? (
                <img data-testid="verify-preview" src={shot} alt="Your selfie" className="h-full w-full object-cover scale-x-[-1]" />
              ) : (
                <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover scale-x-[-1]" />
              )}
            </div>
            {result && !result.verified && (
              <p data-testid="verify-failed" className="text-sm text-pink-200">{result.reason} ({result.attempts_left} tries left today)</p>
            )}
            <div className="flex gap-2">
              {shot ? (
                <>
                  <Button data-testid="verify-retake" variant="outline" onClick={start} disabled={busy} className="flex-1 rounded-full border-white/15"><RotateCcw className="h-4 w-4 mr-1.5" /> Retake</Button>
                  <Button data-testid="verify-submit" onClick={submit} disabled={busy} className="flex-1 btn-gold rounded-full">
                    {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Checking…</> : "Verify me"}
                  </Button>
                </>
              ) : (
                <Button data-testid="verify-capture" onClick={capture} disabled={camErr} className="w-full btn-gold rounded-full"><Camera className="h-4 w-4 mr-1.5" /> Take selfie</Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
